<template>

  <v-container fluid class="px-0 pt-0 shipper-item-edit-details">

    <!-- (data loading) -->
    <template v-if="isDataLoading || isSaving">
      <v-row>
        <v-col class="mt-4 d-flex align-center justify-center" style="height:120px">
          <v-progress-circular indeterminate :size="50" color="primary" />
        </v-col>
      </v-row>
    </template>

    <template v-else-if="shipperToEdit">
      <!---------------->
      <!-- Breadcrumb -->
      <!---------------->
      <v-row class="mt-2 mb-6">
        <v-col cols="12" class="d-flex align-center justify-start page-breadcrumb-new">
          <!-- (breadcrumb) -->
          <v-icon small class="mr-2">mdi-link</v-icon>
          <router-link :to="{ name: 'provider-connection-list' }">
            Shipper Accounts
          </router-link>
          <div class="separator">/</div>
          <div class="active-item"><span class="label-prefix">Edit Location Details:</span> {{ shipperToEdit.name }}</div>
        </v-col>
      </v-row>

      <!--------------------------------->
      <!-- Page Actions (cancel, save) -->
      <!--------------------------------->
      <v-toolbar flat class="item-controls item-editor-controls mb-4">
        <v-btn color="gray" text-color="black" @click="cancel" v-if="isDirty">Cancel</v-btn>
        <v-spacer />
        <v-btn color="primary" width="100" @click="save" v-if="isDirty">Save</v-btn>
      </v-toolbar>

      <!------------------>
      <!-- Edit Details -->
      <!------------------>
      <v-container fluid class="page-content">
          <!-- Name -->
          <v-row class="pa-0 ma-0">
            <v-col cols="12" xs="12" sm="12" md="9" lg="6" class="pa-0 ma-0">
              <div class="page-label">
                <v-text-field
                    style="font-size:26px;"
                    label="Name"
                    v-model.trim="shipperToEdit.name" />
              </div>
            </v-col>
          </v-row>

          <!-- DBA -->
          <v-row class="pa-0 ma-0">
            <v-col cols="12" xs="12" sm="12" md="9" lg="6" class="pa-0 ma-0">
              <v-text-field
                  label="DBA Name"
                  v-model.trim="shipperToEdit.dba" />
            </v-col>
          </v-row>

          <!-- Legal Entity Name -->
          <v-row class="pa-0 ma-0">
            <v-col cols="12" xs="12" sm="12" md="9" lg="6" class="pa-0 ma-0">
              <v-text-field
                  label="Legal Entity Name"
                  v-model.trim="shipperToEdit.legal_entity_name" />
            </v-col>
          </v-row>

          <!-- Phone Numbers -->
          <v-row class="pa-0 mx-0 mt-8 mb-6">
            <v-col cols="10" class="ma-0 pa-0">
              <span class="title">Phone Numbers</span>
            </v-col>
          </v-row>
          <!-- (phone main) -->
          <v-row class="pa-0 ma-0 mt-5">
            <v-col cols="10" xs="10" sm="9" md="5" lg="4" class="ma-0 pa-0">
              <vue-phone-number-input
                  dark
                  clearable
                  no-example
                  :translations="{ phoneNumberLabel: 'Phone Main'}"
                  :no-country-selector="true"
                  default-country-code="US"
                  v-model="shipperToEdit.phone" />
            </v-col>
          </v-row>
          <!-- (phone secondary) -->
          <v-row class="pa-0 ma-0 mt-7">
            <v-col cols="10" xs="10" sm="9" md="5" lg="4" class="ma-0 pa-0">
              <vue-phone-number-input
                  dark
                  clearable
                  no-example
                  :translations="{ phoneNumberLabel: 'Phone Secondary'}"
                  :no-country-selector="true"
                  default-country-code="US"
                  v-model="shipperToEdit.phone_secondary" />
            </v-col>
          </v-row>

          <!-- Appointment Handling -->
          <v-row class="pa-0 mx-0 mt-12 mb-6">
            <v-col cols="12" class="ma-0 pa-0 d-flex align-center justify-start">
              <div class="pr-2 title">Appointment Handling</div>
              <!-- tooltip (about setting appointment requirement) -->
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="primary" style="font-size:22px;cursor:pointer;" v-bind="attrs" v-on="on">
                    mdi-information-outline
                  </v-icon>
                </template>
                <div>This will prevent a pickup from being scheduled until appointment is confirmed by the shipper.</div>
              </v-tooltip>
            </v-col>
          </v-row>
          <!-- (requires appointment selector) -->
          <v-row class="pa-0 ma-0">
            <v-col cols="12" class="ma-0 pa-0">
              <v-switch
                  class="mt-2"
                  label="Requires Appointment?"
                  v-model="shipperToEdit.requires_appointment" />
            </v-col>
          </v-row>
          <!-- (appointment URL) -->
          <v-row class="pa-0 ma-0 mt-2">
            <v-col cols="12" xs="12" sm="12" md="8" lg="6" class="ma-0 pa-0">
              <v-text-field
                  label="Appointment URL (optional)"
                  v-model.trim="shipperToEdit.appointment_url" />
            </v-col>
          </v-row>
          <!-- (notes) -->
          <v-row class="pa-0 ma-0 mt-3">
            <v-col cols="12" xs="12" sm="12" md="8" lg="6" class="ma-0 pa-0">
              <v-textarea
                  rows="1"
                  label="Notes"
                  v-model.trim="shipperToEdit.notes" />
            </v-col>
          </v-row>
      </v-container>
    </template>

  </v-container>

</template>

<script>
import VuePhoneNumberInput from 'vue-phone-number-input'
import 'vue-phone-number-input/dist/vue-phone-number-input.css'

export default {
  name: 'ShipperItemEditDetailsPage',

  components: {
    VuePhoneNumberInput
  },

  props: {
    shipperId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      isDataLoading: false,
      isSaving: false,
      isDirty: true
    }
  },

  computed: {
    uiType () {
      return this.$root.uiType()
    },
    // The active item being edited
    shipperToEdit () {
      return this.$store.getters['sh/itemEditing']('shipper')
    }
  },

  async beforeMount () {
    // Ensure shipperToEdit is loaded
    if (!this.shipperToEdit) {
      this.isDataLoading = true

      const shipper = await this.$store.dispatch('sh/dataApi', {
        uri: `shipper/${this.shipperId}`,
        opts: {
          params: { with: 'recipient_emails' }
        }
      })
      this.$store.dispatch('sh/setItemToEdit', shipper)

      this.isDataLoading = false
    }
  },

  beforeDestroy () {
    // Clear editing registry
    this.$store.dispatch('sh/clearItemEditing', 'shipper')
  },

  methods: {
    cancel () {
      // Return to loc item page
      this.$root.goTo('shipper-item', { shipperId: this.shipperId })
    },

    // In the future, we will stay here in the edit page. Requires usage of the isDirty pattern - |M|
    async save () {
      this.isSaving = true

      // Update shipper
      await this.$store.dispatch('sh/updateData', this.shipperToEdit)

      // Return to item page
      this.$root.goTo('shipper-item', { shipperId: this.shipperId })
    }
  }
}
</script>

<style scoped>
</style>
